import React, { FC, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form as bootstrapForm } from 'react-bootstrap';
import useCloneForm from '../../../hooks/api/form/useCloneForm';
import ModalComponent from '../../../components/modal/ModalComponent';
import Label from '../../../components/library/inputs/label/Label';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import * as Yup from 'yup';
import { Formik, Form, FormikProps } from 'formik';

interface Props {
  open: boolean;
  close: (cloned: boolean) => void;
  formId: string;
  formName: string | undefined;
}

interface Values {
  newFormName: string;
}

const CloneFormModal: FC<Props> = ({ open, close, formId, formName }: Props) => {
  const intl = useIntl();
  const formRef = useRef<FormikProps<Values>>(null);
  const organizationId = useSelector((state: RootState) => state.organization.organization?.id) as string;
  const { cloneForm, isLoading: isLoadingForm, setOrganizationId } = useCloneForm(organizationId);
  console.log(formId);
  useEffect(() => {
    if (organizationId) {
      setOrganizationId(organizationId);
    }
  }, [organizationId]);

  const FormCloneErrorMessages = Yup.object().shape({
    newFormName: Yup.string()
      .min(1, intl.formatMessage({ id: 'formSettings.errorMessage.short' }))
      .max(100, intl.formatMessage({ id: 'formSettings.errorMessage.long' }))
      .required(intl.formatMessage({ id: 'formSettings.errorMessage.required' })),
  });

  return (
    <ModalComponent
      closeSettings={() => close(false)}
      buttons={[
        {
          text: intl.formatMessage({ id: 'cancel' }),
          buttonVariant: 'secondary',
          onClick: () => close(false),
        },
        {
          text: intl.formatMessage({ id: 'confirm' }),
          buttonVariant: 'primary',
          loading: isLoadingForm,
          onClick: () => {
            formRef.current?.submitForm();
          },
        },
      ]}
      show={open}
      modalHeading={intl.formatMessage({
        id: 'formList.cloneFormModal.header',
      })}
      modalSize="lg"
    >
      <div className="container p-5">
        <Formik
          validationSchema={FormCloneErrorMessages}
          initialValues={{
            newFormName: formName + ' - ' + intl.formatMessage({ id: 'formList.cloneFormModal.copy' }),
          }}
          onSubmit={async (values: Values) => {
            if (values.newFormName.length > 0) {
              await cloneForm(formId, values.newFormName);
              close(true);
            }
          }}
          innerRef={formRef}
        >
          {({ errors, touched, values, handleChange, handleBlur }) => (
            <Form>
              <bootstrapForm.Group className="d-flex flex-column justify-content-center align-items-center">
                <Label label={intl.formatMessage({ id: 'formList.cloneFormModal.formName' })}></Label>
                <bootstrapForm.Control
                  type="input"
                  name="newFormName"
                  placeholder={intl.formatMessage({ id: 'formList.cloneFormModal.formName' })}
                  isInvalid={touched.newFormName && !!errors.newFormName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.newFormName}
                />
                <bootstrapForm.Control.Feedback className="text-center" type="invalid">
                  {errors.newFormName && touched.newFormName && (
                    <FormattedMessage id="formList.cloneFormModal.emptyFormName"></FormattedMessage>
                  )}
                </bootstrapForm.Control.Feedback>
              </bootstrapForm.Group>
            </Form>
          )}
        </Formik>
      </div>
    </ModalComponent>
  );
};

export default CloneFormModal;
